import moment from 'moment'

/* Keys
----------------------------------------------------------------------------- */
const GLOBAL_KEYS = {
  Date: 'DATE',
  Year: 'YEAR',
  Project: 'Project'
}

const DATE_TIME_KEYS = {
  Date: 'Date',
  Time: 'Time'
}

const PROJECT_KEYS = {
  Name: 'Name',
  ProjectNumber: 'ProjectNumber',
  Start: 'Start',
  Stop: 'Stop',
  MembersList: 'MembersList',
  MembersListOthers: 'MembersListOthers',
  Manager: 'Manager'
}

const CONTACT_KEYS = {
  FirstName: 'FirstName',
  LastName: 'LastName',
  Company: 'Company',
  Phone: 'Phone',
  Email: 'Email',
  OrganisationNumber: 'OrganisationNumber',
  SocialSecurityNumber: 'SocialSecurityNumber',
  FullAddress: 'FullAddress',
  AddressLine1: 'AddressLine1',
  AddressLine2: 'AddressLine2',
  City: 'City',
  ZipCode: 'ZipCode',
  Country: 'Country',
  StateProvinceRegion: 'StateProvinceRegion'
}


const USER_KEYS = {
  FullName: 'FullName',
  Email: 'Email',
  Contact: 'Contact'
}


const MEMBER_KEYS = {
  Function: 'Function',
  TravelCost: 'TravelCost',
  DietDays: 'DietDays',
  DietPerDay: 'DietPerDay',
  TotalDiet: 'TotalDiet',
  HotelDays: 'HotelDays',
  HotelPerDay: 'HotelPerDay',
  TotalHotels: 'TotalHotels',
  OtherCost: 'OtherCost',
  Wage: 'Wage',
  TotalCost: 'TotalCost'
}

const EVENT_KEYS = {
  City: 'City',
  Venue: 'Venue',
  GetIn: 'GetIn',
  Promoter: 'Promoter',
  CoPromoter: 'CoPromoter',
  Contact: 'Contact',
  Signee: 'Signee',
  TicketsUrl: 'TicketsUrl',
  TicketPrice: 'TicketPrice',
  ContractSum: 'ContractSum',
  VenueUrl: 'VenueUrl',
  Start: 'Start',
  Stop: 'Stop'
}



function getGlobalFullKeys () {
  const globalKeys = []
  const projectKeys = getProjectFullKeys()

  for (const key of Object.values(GLOBAL_KEYS)) {
    switch (key) {
      case GLOBAL_KEYS.Project:
        for (const projectKey of projectKeys) {
          globalKeys.push(`${key}.${projectKey}`)
        }
        break
      default:
        globalKeys.push(key)
        break
    }
  }

  return globalKeys
}

function getDateTimeFullKeys (dateOnly = false) {
  if (dateOnly) {
    return [DATE_TIME_KEYS.Date]
  }

  return Object.values(DATE_TIME_KEYS)
}

function getUserFullKeys () {
  const userKeys = []
  const contactKeys = getContactFullKeys(false)

  for (const key of Object.values(USER_KEYS)) {
    switch (key) {
      case USER_KEYS.Contact:
        for (const contactKey of contactKeys) {
          userKeys.push(`${key}.${contactKey}`)
        }
        break
      default:
        userKeys.push(key)
        break
    }
  }

  return userKeys
}

function getProjectFullKeys () {
  const projectKeys = []
  const userKeys = getUserFullKeys(false)
  const dateTimeKeys = getDateTimeFullKeys(true)

  for (const key of Object.values(PROJECT_KEYS)) {
    switch (key) {
      case PROJECT_KEYS.Manager:
        for (const userKey of userKeys) {
          projectKeys.push(`${key}.${userKey}`)
        }
        break
      case PROJECT_KEYS.Start:
      case PROJECT_KEYS.Stop:
        for (const dateTimeKey of dateTimeKeys) {
          projectKeys.push(`${key}.${dateTimeKey}`)
        }
        break
      default:
        projectKeys.push(key)
        break
    }
  }

  return projectKeys
}

function getContactFullKeys (includeMemberKeys = false) {
  let keys = Object.values(CONTACT_KEYS)
  if (includeMemberKeys) {
    keys = keys.concat(Object.values(MEMBER_KEYS))
  }
  return keys
}

function getEventFullKeys () {
  const eventKeys = []
  const contactKeys = getContactFullKeys(false)
  const dateTimeKeys = getDateTimeFullKeys()
  const projectKeys = getProjectFullKeys()

  for (const key of Object.values(EVENT_KEYS)) {
    switch (key) {
      case EVENT_KEYS.Promoter:
      case EVENT_KEYS.CoPromoter:
      case EVENT_KEYS.Contact:
      case EVENT_KEYS.Signee:
        for (const contactKey of contactKeys) {
          eventKeys.push(`${key}.${contactKey}`)
        }
        break
      case EVENT_KEYS.Start:
      case EVENT_KEYS.Stop:
      case EVENT_KEYS.GetIn:
        for (const dateTimeKey of dateTimeKeys) {
          eventKeys.push(`${key}.${dateTimeKey}`)
        }
        break
      case EVENT_KEYS.Project:
        for (const projectKey of projectKeys) {
          eventKeys.push(`${key}.${projectKey}`)
        }
        break
      default:
        eventKeys.push(key)
        break
    }
  }

  return eventKeys.sort()
}



/* Resolving
----------------------------------------------------------------------------- */
function resolveGlobalPlaceholder ({ project }, placeholder, i = 0) {
  const { key, keyParts, defaultValue } = placeholder

  let value = null
  const keyPart = keyParts.length > i ? keyParts[i] : key

  switch (keyPart) {
    case GLOBAL_KEYS.Date:
      value = moment().format('DD.MM.YYYY') // ('L')
      break
    case GLOBAL_KEYS.Year:
      value = moment().format('YYYY')
      break
    case GLOBAL_KEYS.Project:
      value = resolveProjectPlaceholder(project, placeholder, i + 1)
      break
  }

  value = valueOrDefault(value, defaultValue)

  return value
}


function resolveDateTimePlaceholder (dateTime, placeholder, i = 0) {
  const { key, keyParts, defaultValue } = placeholder

  if (!dateTime) return valueOrDefault(null, defaultValue)

  let value = null
  const keyPart = keyParts.length > i ? keyParts[i] : key
  const dateTimeMoment = moment.utc(dateTime)

  switch (keyPart) {
    case DATE_TIME_KEYS.Date:
      value = dateTimeMoment.format('DD.MM.YYYY') // ('L')
      break
    case DATE_TIME_KEYS.Time:
      value = dateTimeMoment.format('HH:mm') // ('LT')
      break
    default:
      value = dateTimeMoment.format('DD.MM.YYYY HH:mm')
      break
  }

  value = valueOrDefault(value, defaultValue)

  return value
}


function resolveUserPlaceholder (user, placeholder, i = 0) {
  const { key, keyParts, defaultValue } = placeholder

  if (!user) return valueOrDefault(null, defaultValue)

  let value = null
  const keyPart = keyParts.length > i ? keyParts[i] : key

  switch (keyPart) {
    case USER_KEYS.FullName:
      value = user.fullName
      break
    case USER_KEYS.Email:
      value = user.email
      break
    case USER_KEYS.Contact:
      if (user.contact) {
        value = resolveContactPlaceholder(user.contact, placeholder, i + 1)
      }
      break
  }

  value = valueOrDefault(value, defaultValue)

  return value
}


function resolveProjectPlaceholder (project, placeholder, i = 0) {
  const { key, keyParts, defaultValue } = placeholder

  if (!project) return valueOrDefault(null, defaultValue)

  let value = null
  const keyPart = keyParts.length > i ? keyParts[i] : key

  switch (keyPart) {
    case PROJECT_KEYS.Name:
      value = project.title
      break
    case PROJECT_KEYS.ProjectNumber:
      value = project.projectNumber
      break
    case PROJECT_KEYS.Start:
      value = resolveDateTimePlaceholder(project.startUtc, placeholder, i + 1)
      break
    case PROJECT_KEYS.Stop:
      value = resolveDateTimePlaceholder(project.stopUtc, placeholder, i + 1)
      break
    case PROJECT_KEYS.MembersList:
      value = project.members.map(member => member.contact.displayName).join(', ')
      break
    case PROJECT_KEYS.MembersListOthers:
      value = project.otherMembers?.map(member => member.contact.displayName).join(', ')
      break
    case PROJECT_KEYS.Manager:
      if (project.manager) {
        value = resolveUserPlaceholder(project.manager, placeholder, i + 1)
      }
      break
  }

  value = valueOrDefault(value, defaultValue)

  return value
}


function resolveContactPlaceholder (contact, placeholder, i = 0) {
  const { key, keyParts, defaultValue } = placeholder

  if (!contact) return valueOrDefault(null, defaultValue)

  let value = null
  const keyPart = keyParts.length > i ? keyParts[i] : key

  switch (keyPart) {
    case CONTACT_KEYS.FirstName:
      value = contact.firstName
      break
    case CONTACT_KEYS.LastName:
      value = contact.lastName
      break
    case CONTACT_KEYS.Company:
      value = contact.company
      break
    case CONTACT_KEYS.Phone:
      value = contact.phone?.phone
      break
    case CONTACT_KEYS.Email:
      value = contact.email?.email
      break
    case CONTACT_KEYS.OrganisationNumber:
      value = contact.organisationNumber
      break
    case CONTACT_KEYS.SocialSecurityNumber:
      value = contact.socialSecurityNumber
      break
    case CONTACT_KEYS.FullAddress:
      value = contact.fullAddress
      break
    case CONTACT_KEYS.AddressLine1:
      value = contact.address?.addressLine1
      break
    case CONTACT_KEYS.AddressLine2:
      value = contact.address?.addressLine2
      break
    case CONTACT_KEYS.City:
      value = contact.address?.city
      break
    case CONTACT_KEYS.Country:
      value = contact.address?.country
      break
    case CONTACT_KEYS.ZipCode:
      value = contact.address?.zipCode
      break
    case CONTACT_KEYS.StateProvinceRegion:
      value = contact.address?.stateProvinceRegion
      break
    default:
      if (contact.member) {
        value = resolveMemberPlaceholder(contact.member, placeholder, i)
      }
      break
  }

  value = valueOrDefault(value, defaultValue)

  return value
}


function resolveMemberPlaceholder (member, placeholder, i = 0) {
  const { key, keyParts, defaultValue } = placeholder

  if (!member) return valueOrDefault(null, defaultValue)

  let value = null
  const keyPart = keyParts.length > i ? keyParts[i] : key

  switch (keyPart) {
    case MEMBER_KEYS.Function:
      value = member.function
      break
    case MEMBER_KEYS.TravelCost:
      value = member.travelCost
      break
    case MEMBER_KEYS.DietDays:
      value = member.dietDays
      break
    case MEMBER_KEYS.DietPerDay:
      value = member.dietPerDay
      break
    case MEMBER_KEYS.TotalDiet:
      value = member.totalDiet
      break
    case MEMBER_KEYS.HotelDays:
      value = member.hotelDays
      break
    case MEMBER_KEYS.HotelPerDay:
      value = member.hotelPerDay
      break
    case MEMBER_KEYS.TotalHotels:
      value = member.totalHotels
      break
    case MEMBER_KEYS.OtherCost:
      value = member.otherCost
      break
    case MEMBER_KEYS.Wage:
      value = member.wage
      break
    case MEMBER_KEYS.TotalCost:
      value = member.totalCost
      break
  }

  value = valueOrDefault(value, defaultValue)

  return value
}


function resolveEventPlaceholder (event, placeholder, i = 0) {
  const { key, keyParts, defaultValue } = placeholder

  if (!event) return valueOrDefault(null, defaultValue)

  let value = null
  const keyPart = keyParts.length > i ? keyParts[i] : key

  switch (keyPart) {
    case EVENT_KEYS.City:
      value = event.city
      break
    case EVENT_KEYS.Venue:
      value = event.venue
      break
    case EVENT_KEYS.VenueUrl:
      value = event.venueUrl
      break
    case EVENT_KEYS.TicketsUrl:
      value = event.ticketsUrl
      break
    case EVENT_KEYS.TicketPrice:
      value = event.ticketPrice
      break
    case EVENT_KEYS.ContractSum:
      value = event.contractSum
      break
    case EVENT_KEYS.GetIn:
      value = resolveDateTimePlaceholder(event.getIn, placeholder, i + 1)
      break
    case EVENT_KEYS.Start:
      value = resolveDateTimePlaceholder(event.start, placeholder, i + 1)
      break
    case EVENT_KEYS.Stop:
      value = resolveDateTimePlaceholder(event.stop, placeholder, i + 1)
      break
    case EVENT_KEYS.Promoter:
      value = resolveContactPlaceholder(event.promoter, placeholder, i + 1)
      break
    case EVENT_KEYS.CoPromoter:
      value = resolveContactPlaceholder(event.coPromoter, placeholder, i + 1)
      break
    case EVENT_KEYS.Contact:
      value = resolveContactPlaceholder(event.contact, placeholder, i + 1)
      break
    case EVENT_KEYS.Signee:
      value = resolveContactPlaceholder(event.signee, placeholder, i + 1)
      break
  }

  value = valueOrDefault(value, defaultValue)

  return value
}


function valueOrDefault (value, defaultValue) {
  if (value === null || value === '' || value === undefined) {
    value = defaultValue
  }
  if (value === '' || value === undefined) {
    value = null
  }
  if (value !== null && value !== undefined) {
    value = String(value)
  }
  return value
}



/* Formatting & Parsing
----------------------------------------------------------------------------- */
/* valid placeholder examples:
  {{name:key.subkey|default}}
  {{key.subkey|default}}
  {{key}}
*/

const PLACEHOLDER_REGEX = /{{[\w+:]*\w+[.\w+]*\|*\w+}}/g


function formatPlaceholder ({ name, key, defaultValue }) {
  let placeholder = name ? `${name}:${key}` : key

  if (defaultValue) {
    placeholder += `|${defaultValue}`
  }

  return '{{' + placeholder + '}}'
}


function parsePlaceholder (placeholder) {
  let p = placeholder.replace(/[{}]/g, '')

  const nameSplit = p.split(':')
  let name = null
  if (nameSplit.length > 1) {
    name = nameSplit[0]
    p = nameSplit[1]
  }

  const defaultValueSplit = p.split('|')
  let defaultValue = ''
  if (defaultValueSplit.length > 1) {
    defaultValue = defaultValueSplit[1]
    p = defaultValueSplit[0]
  }

  const key = p
  const keySplit = p.split('.')

  const placeholderObj = {
    expression: placeholder,
    name: name,
    key: key,
    keyParts: keySplit,
    defaultValue: defaultValue
  }

  return placeholderObj
}


function parsePlaceholdersFromSfdt (sfdt) {
  // sfdt is JSON where we need to concat only the "text" prop values to get raw text without formatting
  const textRegex = /(?<="text":").+?(?=(?<!\\)")/g
  const text = sfdt.match(textRegex)?.join('') || ''

  const placeholders = []
  const matches = text.match(PLACEHOLDER_REGEX)

  if (matches) {
    for (const match of matches) {
      const placeholder = parsePlaceholder(match)
      placeholders.push(placeholder)
    }
  }

  return placeholders
}


function containsKeyStartingWith (placeholders, keyStart) {
  return placeholders.find(x => x.key.startsWith(keyStart)) !== undefined
}



export default {
  GLOBAL_KEYS,
  DATE_TIME_KEYS,
  PROJECT_KEYS,
  EVENT_KEYS,
  CONTACT_KEYS,
  MEMBER_KEYS,

  formatPlaceholder,
  parsePlaceholder,
  parsePlaceholdersFromSfdt,
  containsKeyStartingWith,

  getGlobalFullKeys,
  getContactFullKeys,
  getEventFullKeys,

  resolveGlobalPlaceholder,
  resolveContactPlaceholder,
  resolveEventPlaceholder
}
